import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerPrivateRegionAgentInstance } from '@/api/type-server'
import { Button, Modal, Trans } from '@/components'
import { message } from '@/components/base/Message'
import { InstanceStatus } from '@/models/instance'

import { ActiveInstancesModal } from './ActiveInstancesModal'

interface StandbyConfirmModalProps {
  open: boolean
  mode: 'standby' | 'online'
  resID: string
  onClose: () => void
  onConfirm: () => Promise<void>
}

const i18nKeys = {
  standby: {
    title: 'nodeDetail.switchToStandbyTitle',
    content: 'nodeDetail.switchToStandbyContent',
    confirm: 'nodeDetail.switchToStandby',
  },
  online: {
    title: 'nodeDetail.switchToOnlineTitle',
    content: 'nodeDetail.switchToOnlineContent',
    confirm: 'nodeDetail.switchToOnline',
  },
} as const

export function StandbyConfirmModal({
  open,
  mode,
  resID,
  onClose,
  onConfirm,
}: StandbyConfirmModalProps) {
  const { t } = useTranslation('privateRegion')
  const keys = i18nKeys[mode]
  const [loading, setLoading] = useState(false)
  const [activeInstances, setActiveInstances] = useState<ServerPrivateRegionAgentInstance[]>([])

  const handleConfirm = async () => {
    if (mode === 'standby') {
      setLoading(true)
      try {
        const res = await API.getPrivateRegionAgentInstances({
          res_id: resID,
          statuses: [InstanceStatus.Pending, InstanceStatus.Running],
        })
        const instances = res.instances || []
        if (instances.length > 0) {
          setActiveInstances(instances)
        } else {
          await onConfirm()
        }
      } catch (err) {
        message.error(t('errors.failedToCheckInstances'))
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(true)
      try {
        await onConfirm()
      } finally {
        setLoading(false)
      }
    }
  }

  const handleClose = () => {
    setActiveInstances([])
    onClose()
  }

  if (activeInstances.length > 0) {
    return <ActiveInstancesModal open={open} instances={activeInstances} onClose={handleClose} />
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Modal.Header>
        <Trans ns="privateRegion" i18nKey={keys.title} />
      </Modal.Header>
      <Modal.Body>
        <div className="t-medium">
          <Trans ns="privateRegion" i18nKey={keys.content} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={handleClose} disabled={loading}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type={mode === 'standby' ? 'danger' : 'secondary'}
          onClick={handleConfirm}
          disabled={loading}
        >
          <Trans ns="privateRegion" i18nKey={keys.confirm} />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
